import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import {
  EditClientDialogData,
  EditClientRequest,
  EditClientForm,
  OptionalInterfacesForm,
  CLIENT_PLAN_OPTIONS,
  LINK_PREVIEW_MODES,
  NAME_PRONUNCIATION_SCOPES,
  NAME_PRONUNCIATION_ROLE_OPTIONS,
  BILLING_MODES,
  DEFAULT_OPTIONAL_INTERFACES,
  type BillingMode,
  type ClientPlanId,
  type NamePronunciationRole,
  type OptionalInterfaces,
} from "../client.interface";
import { ClientService } from "../client.service";
import { validateFutureDate } from "../../shared/validators/date.validator";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";

@Component({
  selector: "app-client-edit-dialog",
  templateUrl: "./client-edit-dialog.component.html",
  styleUrls: ["./client-edit-dialog.component.scss"],
  standalone: false,
})
export class ClientEditDialogComponent implements OnInit {
  form: FormGroup<EditClientForm>;
  oldDomain: string;
  sendingRequest = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  readonly subscriptions = CLIENT_PLAN_OPTIONS;
  readonly linkPreviewModes = LINK_PREVIEW_MODES;
  readonly namePronunciationScopes = NAME_PRONUNCIATION_SCOPES;
  readonly namePronunciationRoleOptions = NAME_PRONUNCIATION_ROLE_OPTIONS;
  readonly billingModes = BILLING_MODES;

  private readonly trialFinishDateValidations = [
    (c: FormControl) => Validators.required(c),
    validateFutureDate,
  ];

  constructor(
    public dialogRef: MatDialogRef<ClientEditDialogComponent>,
    private clientService: ClientService,
    @Inject(MAT_DIALOG_DATA) public data: EditClientDialogData,
  ) {
    this.dialogRef.updateSize("720px");

    this.oldDomain = data.domain;
    const oi: OptionalInterfaces =
      data.optionalInterfaces ?? DEFAULT_OPTIONAL_INTERFACES;
    const billingMode: BillingMode = data.billingMode ?? "default";
    const planId: ClientPlanId | null =
      data.planId === "free" ||
      data.planId === "freemium" ||
      data.planId === "paid" ||
      data.planId === "partner" ||
      data.planId === "reseller"
        ? data.planId
        : null;

    this.form = new FormGroup<EditClientForm>({
      domain: new FormControl(data.domain, {
        nonNullable: true,
        validators: [(c) => Validators.required(c)],
      }),
      domainChangeAcknowledged: new FormControl(false, { nonNullable: true }),
      googleAdminEmail: new FormControl(data.googleAdminEmail ?? null, [
        (c) => Validators.email(c),
      ]),
      planId: new FormControl<ClientPlanId | null>(planId, [
        (c) => Validators.required(c),
      ]),
      trialFinishDate: new FormControl(
        data.trialFinishDate ?? null,
        planId === "free" ? this.trialFinishDateValidations : [],
      ),
      excludedNewEmployees: new FormControl(
        data.excludedNewEmployees || false,
        {
          nonNullable: true,
        },
      ),
      language: new FormControl(data.language || "en", {
        nonNullable: true,
        validators: [(c) => Validators.required(c)],
      }),
      billingMode: new FormControl<BillingMode>(billingMode, {
        nonNullable: true,
        validators: [(c) => Validators.required(c)],
      }),
      optionalInterfaces: new FormGroup<OptionalInterfacesForm>({
        digitalCard: new FormControl(oi.digitalCard, { nonNullable: true }),
        mobileApps: new FormControl(oi.mobileApps, { nonNullable: true }),
        browserExtension: new FormControl(oi.browserExtension, {
          nonNullable: true,
        }),
        appleWallet: new FormControl(oi.appleWallet, { nonNullable: true }),
        googleWallet: new FormControl(oi.googleWallet, { nonNullable: true }),
        emailSignatures: new FormControl(oi.emailSignatures, {
          nonNullable: true,
        }),
        virtualBackgrounds: new FormControl(oi.virtualBackgrounds, {
          nonNullable: true,
        }),
        linkPreviewMode: new FormControl(oi.linkPreviewMode, {
          nonNullable: true,
        }),
        selfPrintCards: new FormControl(oi.selfPrintCards, {
          nonNullable: true,
        }),
        contextualQr: new FormControl(oi.contextualQr, { nonNullable: true }),
        namePronunciation: new FormControl(oi.namePronunciation, {
          nonNullable: true,
        }),
        namePronunciationScope: new FormControl(oi.namePronunciationScope, {
          nonNullable: true,
        }),
        namePronunciationRoles: new FormControl<NamePronunciationRole[]>(
          oi.namePronunciationRoles.filter(
            (role): role is NamePronunciationRole =>
              (NAME_PRONUNCIATION_ROLE_OPTIONS as readonly string[]).includes(
                role,
              ),
          ),
          { nonNullable: true },
        ),
      }),
    });
  }

  get isDomainChanged(): boolean {
    const current = this.form.controls.domain.value.trim();
    return current !== this.oldDomain.trim();
  }

  submit() {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    if (
      this.isDomainChanged &&
      !this.form.controls.domainChangeAcknowledged.value
    ) {
      return;
    }
    const raw = this.form.getRawValue();
    if (raw.planId == null) {
      return;
    }
    this.sendingRequest = true;
    const value: EditClientRequest = {
      domain: raw.domain,
      googleAdminEmail: raw.googleAdminEmail ?? "",
      planId: raw.planId,
      isIncludedDemoContent: false,
      trialFinishDate: raw.trialFinishDate,
      excludedNewEmployees: raw.excludedNewEmployees,
      language: raw.language,
      billingMode: raw.billingMode,
      optionalInterfaces: {
        digitalCard: raw.optionalInterfaces.digitalCard,
        mobileApps: raw.optionalInterfaces.mobileApps,
        browserExtension: raw.optionalInterfaces.browserExtension,
        appleWallet: raw.optionalInterfaces.appleWallet,
        googleWallet: raw.optionalInterfaces.googleWallet,
        emailSignatures: raw.optionalInterfaces.emailSignatures,
        virtualBackgrounds: raw.optionalInterfaces.virtualBackgrounds,
        linkPreviewMode: raw.optionalInterfaces.linkPreviewMode,
        selfPrintCards: raw.optionalInterfaces.selfPrintCards,
        contextualQr: raw.optionalInterfaces.contextualQr,
        namePronunciation: raw.optionalInterfaces.namePronunciation,
        namePronunciationScope: raw.optionalInterfaces.namePronunciationScope,
        namePronunciationRoles:
          raw.optionalInterfaces.namePronunciationScope === "designated_roles"
            ? raw.optionalInterfaces.namePronunciationRoles
            : [],
      },
    };

    this.clientService.edit(this.oldDomain, value).subscribe(
      () => this.close(true),
      () => {
        this.sendingRequest = false;
      },
    );
  }

  close(success?: boolean) {
    this.dialogRef.close(success);
  }

  ngOnInit() {
    this.form.controls.domain.valueChanges.subscribe(() => {
      if (!this.isDomainChanged) {
        this.form.controls.domainChangeAcknowledged.setValue(false);
      }
    });
    this.form.controls.planId.valueChanges.subscribe((value) => {
      if (value === "free") {
        this.form.controls.trialFinishDate.setValidators(
          this.trialFinishDateValidations,
        );
      } else {
        this.form.controls.trialFinishDate.clearValidators();
        this.form.controls.trialFinishDate.setValue(null);
      }

      this.form.controls.trialFinishDate.updateValueAndValidity();
    });
  }
}
